import { useMemo } from 'react';
import { ImageIcon, Film, User, Type } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { getEffectiveSceneValues } from '@/@types/reelScenes';
import type { IMediaPreviewProps, IMediaContentProps } from '@/@types/sceneEditorV2';
import type { VideoThumbnailSelection } from '@/@types/sceneEditorRedesign';

/** Consistent media frame classes used across all display modes */
const FRAME_CLASSES = 'aspect-[9/16] w-64';

const SCENE_TYPE_LABELS: Record<string, string> = {
  avatar_only: 'Avatar Only',
  video_only: 'Video Only',
  avatar_broll_split: 'Avatar + Video',
  avatar_image_split: 'Avatar + Image',
};

function ThumbnailButton({
  imageUrl,
  label,
  isActive,
  onClick,
}: {
  imageUrl: string | null;
  label: string;
  isActive: boolean;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className={cn(
        'flex h-[110px] w-[62px] flex-col items-center justify-center overflow-hidden rounded',
        isActive ? 'border-2 border-primary' : 'border border-border'
      )}
    >
      {imageUrl ? (
        <img src={imageUrl} alt={label} className="h-full w-full object-cover" />
      ) : (
        <ImageIcon className="h-5 w-5 text-muted-foreground" />
      )}
    </button>
  );
}

function FramePlaceholder() {
  return (
    <div className={cn('flex items-center justify-center rounded-lg bg-muted', FRAME_CLASSES)}>
      <ImageIcon className="h-10 w-10 text-muted-foreground" />
    </div>
  );
}

function FrameImage({ url, alt }: { url: string; alt: string }) {
  return (
    <div className={cn('overflow-hidden rounded-lg border border-border', FRAME_CLASSES)}>
      <img src={url} alt={alt} className="h-full w-full object-cover" />
    </div>
  );
}

function FrameVideo({ url }: { url: string }) {
  return (
    <div className={cn('overflow-hidden rounded-lg border border-border bg-black', FRAME_CLASSES)}>
      <video src={url} controls className="h-full w-full object-contain" />
    </div>
  );
}

function FrameSkeleton({ label }: { label?: string }) {
  return (
    <div className={cn('relative overflow-hidden rounded-lg', FRAME_CLASSES)}>
      <Skeleton className="absolute inset-0 rounded-lg bg-muted" />
      {label && (
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="text-xs text-muted-foreground">{label}</span>
        </div>
      )}
    </div>
  );
}

function MediaContent({
  scene,
  sceneType,
  frameMode,
  firstFrameUrl,
  lastFrameUrl,
  videoUrl,
  avatarImageUrl,
  selectedThumbnail,
  isGeneratingImages,
  isGeneratingVideo,
}: IMediaContentProps) {
  if (!scene) {
    return (
      <div className="flex flex-col items-center gap-3 text-muted-foreground">
        <Film className="h-14 w-14" />
        <span className="text-base">Select a scene</span>
      </div>
    );
  }

  if (sceneType === 'avatar_only') {
    if (videoUrl) return <FrameVideo url={videoUrl} />;
    if (isGeneratingVideo) return <FrameSkeleton label="Generating video..." />;
    if (avatarImageUrl) return <FrameImage url={avatarImageUrl} alt="AI Avatar" />;
    return (
      <div className="flex flex-col items-center gap-3 text-muted-foreground">
        <User className="h-14 w-14" />
        <span className="text-base">Select an AI Avatar</span>
      </div>
    );
  }

  if (sceneType === 'avatar_broll_split') {
    // Show final composite video if ready
    if (scene.videoGenerationStatus === 'completed' && videoUrl) {
      return <FrameVideo url={videoUrl} />;
    }
    if (isGeneratingVideo) return <FrameSkeleton label="Generating video..." />;
    if (isGeneratingImages) return <FrameSkeleton label="Regenerating frames..." />;

    // Split preview: top half + bottom half
    const effectiveTextCardTitle = scene.editedTextCardTitle ?? scene.textCardTitle;
    const topContent = scene.splitTopContent ?? 'broll';
    return (
      <div
        className={cn(
          'flex flex-col overflow-hidden rounded-lg border border-border',
          FRAME_CLASSES
        )}
      >
        {/* Top section: b-roll image or text card (16:9 aspect ratio) */}
        <div className="relative aspect-video w-full flex-none overflow-hidden">
          {topContent === 'broll' && firstFrameUrl ? (
            <img src={firstFrameUrl} alt="B-Roll" className="h-full w-full object-cover" />
          ) : topContent === 'text_card' && effectiveTextCardTitle ? (
            <div className="flex h-full w-full items-center justify-center bg-gradient-to-b from-gray-800 to-gray-900 p-3">
              <span className="text-center text-sm font-bold text-white">
                {effectiveTextCardTitle}
              </span>
            </div>
          ) : (
            <div className="flex h-full w-full items-center justify-center bg-muted">
              {topContent === 'text_card' ? (
                <Type className="h-8 w-8 text-muted-foreground" />
              ) : (
                <Film className="h-8 w-8 text-muted-foreground" />
              )}
            </div>
          )}
        </div>

        {/* Separator line */}
        <div className="h-px w-full bg-border" />

        {/* Bottom section: avatar preview (fills remaining space) */}
        <div className="relative min-h-0 flex-1 overflow-hidden">
          {avatarImageUrl ? (
            <img src={avatarImageUrl} alt="AI Avatar" className="h-full w-full object-cover" />
          ) : (
            <div className="flex h-full w-full items-center justify-center bg-muted">
              <User className="h-8 w-8 text-muted-foreground" />
            </div>
          )}
        </div>
      </div>
    );
  }

  if (sceneType === 'avatar_image_split') {
    // Show final composite video if ready
    if (scene.videoGenerationStatus === 'completed' && videoUrl) {
      return <FrameVideo url={videoUrl} />;
    }
    if (isGeneratingVideo) return <FrameSkeleton label="Generating video..." />;
    if (isGeneratingImages) return <FrameSkeleton label="Regenerating image..." />;

    // Split preview: static image top + avatar bottom
    return (
      <div
        className={cn(
          'flex flex-col overflow-hidden rounded-lg border border-border',
          FRAME_CLASSES
        )}
      >
        {/* Top section: static image (16:9 aspect ratio) */}
        <div className="relative aspect-video w-full flex-none overflow-hidden">
          {firstFrameUrl ? (
            <img src={firstFrameUrl} alt="Scene Image" className="h-full w-full object-cover" />
          ) : (
            <div className="flex h-full w-full items-center justify-center bg-muted">
              <ImageIcon className="h-8 w-8 text-muted-foreground" />
            </div>
          )}
        </div>

        {/* Separator line */}
        <div className="h-px w-full bg-border" />

        {/* Bottom section: avatar preview (fills remaining space) */}
        <div className="relative min-h-0 flex-1 overflow-hidden">
          {avatarImageUrl ? (
            <img src={avatarImageUrl} alt="AI Avatar" className="h-full w-full object-cover" />
          ) : (
            <div className="flex h-full w-full items-center justify-center bg-muted">
              <User className="h-8 w-8 text-muted-foreground" />
            </div>
          )}
        </div>
      </div>
    );
  }

  if (videoUrl) {
    if (selectedThumbnail === 'video') return <FrameVideo url={videoUrl} />;
    const frameUrl =
      selectedThumbnail === 'first-frame' || frameMode === 'start-only'
        ? firstFrameUrl
        : lastFrameUrl;
    if (frameUrl)
      return (
        <FrameImage
          url={frameUrl}
          alt={selectedThumbnail === 'first-frame' ? 'First Frame' : 'Last Frame'}
        />
      );
    return <FramePlaceholder />;
  }

  if (isGeneratingVideo) return <FrameSkeleton label="Generating video..." />;

  if (firstFrameUrl) {
    return (
      <div className="flex items-start justify-center gap-5">
        <FrameImage url={firstFrameUrl} alt="Start Frame" />
        {frameMode === 'start-end' && lastFrameUrl && (
          <FrameImage url={lastFrameUrl} alt="End Frame" />
        )}
        {frameMode === 'start-end' && !lastFrameUrl && <FramePlaceholder />}
      </div>
    );
  }

  if (isGeneratingImages) {
    return (
      <div className="flex items-start justify-center gap-5">
        <FrameSkeleton label="Generating frames..." />
        {frameMode === 'start-end' && <FrameSkeleton label="Generating frames..." />}
      </div>
    );
  }

  return (
    <div className="flex items-start justify-center gap-5">
      <FramePlaceholder />
      {frameMode === 'start-end' && <FramePlaceholder />}
    </div>
  );
}

export function MediaPreview({
  scene,
  frameMode,
  firstFrameUrl,
  lastFrameUrl,
  videoUrl,
  avatarImageUrl,
  selectedThumbnail,
  onSelectThumbnail,
  isGeneratingImages,
  isGeneratingVideo,
}: IMediaPreviewProps) {
  const sceneType = useMemo(() => {
    if (!scene) return null;
    return getEffectiveSceneValues(scene).sceneType;
  }, [scene]);

  const thumbnails: { id: VideoThumbnailSelection; url: string | null; label: string }[] = [
    { id: 'first-frame', url: firstFrameUrl, label: 'First Frame' },
    ...(frameMode === 'start-end'
      ? [{ id: 'last-frame' as VideoThumbnailSelection, url: lastFrameUrl, label: 'Last Frame' }]
      : []),
    { id: 'video', url: firstFrameUrl, label: 'Video' },
  ];

  return (
    <div className="flex flex-1 flex-col">
      {sceneType && (
        <div className="px-4 pt-2">
          <span className="text-xs font-medium text-muted-foreground">
            {SCENE_TYPE_LABELS[sceneType] ?? sceneType}
          </span>
        </div>
      )}
      <div className="relative flex flex-1 items-center justify-center gap-4 p-4">
        {videoUrl &&
          sceneType !== 'avatar_only' &&
          sceneType !== 'avatar_broll_split' &&
          sceneType !== 'avatar_image_split' && (
            <div className="flex flex-col gap-2">
              {thumbnails.map(thumb => (
                <ThumbnailButton
                  key={thumb.id}
                  imageUrl={thumb.url}
                  label={thumb.label}
                  isActive={selectedThumbnail === thumb.id}
                  onClick={() => onSelectThumbnail(thumb.id)}
                />
              ))}
            </div>
          )}
        <div className="flex flex-1 items-center justify-center gap-4">
          <MediaContent
            scene={scene}
            sceneType={sceneType}
            frameMode={frameMode}
            firstFrameUrl={firstFrameUrl}
            lastFrameUrl={lastFrameUrl}
            videoUrl={videoUrl}
            avatarImageUrl={avatarImageUrl}
            selectedThumbnail={selectedThumbnail}
            isGeneratingImages={isGeneratingImages}
            isGeneratingVideo={isGeneratingVideo}
          />
        </div>
      </div>
    </div>
  );
}
